/**
 * Field Security Configuration for Participant Data
 * 
 * This file maps each field from fieldMappings.ts to its security requirements
 * based on the security patterns found in show_full.php files across different programs.
 */

import { hasAnyPolicy, normalizePolicyList } from '@/utils/policies';

export type SecurityRole = 'admin' | 'provider' | 'provider+' | 'user' | 'none';
export type AccessLevel = 'read' | 'write' | 'none';

export interface FieldSecurityRule {
  /** Field name from fieldMappings.ts */
  fieldName: string;
  /** Description of the field */
  description: string;
  /** Required roles for read access */
  readRoles: SecurityRole[];
  /** Required roles for write access */
  writeRoles: SecurityRole[];
  /** Associated PHP page that controls this field */
  phpPage?: string;
  /** Whether this field requires admin role specifically */
  requiresAdmin: boolean;
  /** Whether this field can be edited by the user who created it */
  userOwnership: boolean;
  /** Additional security notes */
  notes?: string;
}

/**
 * Security configuration for all participant fields
 */
export const FIELD_SECURITY: Record<string, FieldSecurityRule> = {
  // === HIGH SECURITY FIELDS (Admin only) ===
  
  ssn: {
    fieldName: 'ssn',
    description: 'Client SSN', 
    readRoles: ['admin'],
    writeRoles: ['admin'],
    requiresAdmin: true,
    userOwnership: false,
    notes: 'ODR only, highly sensitive, writePolicy: odr'
  },

  pcode: {
    fieldName: 'pcode',
    description: 'Client PCode',
    readRoles: ['admin'],
    writeRoles: ['none'],
    requiresAdmin: true,
    userOwnership: false,
    notes: 'ODR only, writePolicy: none'
  },

  acuity: {
    fieldName: 'acuity',
    description: 'Client Acuity',
    readRoles: ['admin'],
    writeRoles: ['admin'],
    requiresAdmin: true,
    userOwnership: false,
    notes: 'ODR only, writePolicy: odr'
  },

  cin: {
    fieldName: 'cin',
    description: 'Client CIN',
    readRoles: ['admin'],
    writeRoles: ['admin'],
    requiresAdmin: true,
    userOwnership: false,
    notes: 'ODR only, writePolicy: odr'
  },

  cii: {
    fieldName: 'cii', 
    description: 'Client CII',
    readRoles: ['admin'],
    writeRoles: ['admin'],
    requiresAdmin: true,
    userOwnership: false,
    notes: 'ODR only, writePolicy: odr'
  },

  fin: {
    fieldName: 'fin',
    description: 'Client FIN',
    readRoles: ['admin'],
    writeRoles: ['admin'],
    requiresAdmin: true,
    userOwnership: false,
    notes: 'ODR only, writePolicy: odr'
  },

  mainNumber: {
    fieldName: 'mainNumber',
    description: 'Client Main Number',
    readRoles: ['admin'],
    writeRoles: ['admin'], 
    requiresAdmin: true,
    userOwnership: false,
    notes: 'ODR only, writePolicy: odr'
  },

  medicalDemographicsNotes: {
    fieldName: 'medicalDemographicsNotes',
    description: 'Client Medical Demographics Notes',
    readRoles: ['admin'],
    writeRoles: ['admin'],
    phpPage: 'show_full_medical.php',
    requiresAdmin: true,
    userOwnership: false,
    notes: 'ODR only, requires admin role check via security_usergroups, writePolicy: odr'
  },

  candidatesData: {
    fieldName: 'candidatesData',
    description: 'Candidates List Data',
    readRoles: ['admin'],
    writeRoles: ['none'],
    requiresAdmin: true,
    userOwnership: false,
    notes: 'ODR only, write policy: none'
  },

  active: {
    fieldName: 'active',
    description: 'Active Status',
    readRoles: ['admin'],
    writeRoles: ['none'],
    requiresAdmin: true,
    userOwnership: false,
    notes: 'ODR only, computed field'
  },

  // === MEDICAL FIELDS (Admin + Provider) ===

  currentMeds: {
    fieldName: 'currentMeds',
    description: 'Client Current Medications',
    readRoles: ['admin', 'provider'],
    writeRoles: ['admin', 'provider'],
    phpPage: 'show_full_medical.php',
    requiresAdmin: false,
    userOwnership: false,
    notes: 'Legacy only, medical section, writePolicy: all'
  },

  psychHistory: {
    fieldName: 'psychHistory',
    description: 'Client Psych History',
    readRoles: ['admin', 'provider'],
    writeRoles: ['admin', 'provider'],
    phpPage: 'show_full_medical.php',
    requiresAdmin: false,
    userOwnership: false,
    notes: 'Legacy only, medical section, writePolicy: all'
  },

  psychotropicMeds: {
    fieldName: 'psychotropicMeds',
    description: 'Client Psychotropic Medications',
    readRoles: ['admin', 'provider'],
    writeRoles: ['admin', 'provider'],
    phpPage: 'show_full_medical.php',
    requiresAdmin: false,
    userOwnership: false,
    notes: 'Legacy only, medical section, writePolicy: all'
  },

  medicalProblems: {
    fieldName: 'medicalProblems',
    description: 'Client Medical Problems',
    readRoles: ['admin', 'provider'],
    writeRoles: ['admin', 'provider'],
    phpPage: 'show_full_medical.php',
    requiresAdmin: false,
    userOwnership: false,
    notes: 'Legacy only, medical section, writePolicy: all'
  },

  substanceHistory: {
    fieldName: 'substanceHistory',
    description: 'Client Substance History',
    readRoles: ['admin', 'provider'],
    writeRoles: ['admin', 'provider'],
    phpPage: 'show_full_medical.php',
    requiresAdmin: false,
    userOwnership: false,
    notes: 'Legacy only, medical section, writePolicy: all'
  },

  adherence: {
    fieldName: 'adherence',
    description: 'Client Adherence',
    readRoles: ['admin', 'provider'],
    writeRoles: ['admin', 'provider'],
    phpPage: 'show_full_medical.php',
    requiresAdmin: false,
    userOwnership: false,
    notes: 'Legacy only, writePolicy: all'
  },

  clinicalPresentation: {
    fieldName: 'clinicalPresentation',
    description: 'Client Clinical Presentation',
    readRoles: ['admin', 'provider'],
    writeRoles: ['admin', 'provider'],
    phpPage: 'show_full_medical.php',
    requiresAdmin: false,
    userOwnership: false,
    notes: 'Legacy only, writePolicy: all'
  },

  otherMeds: {
    fieldName: 'otherMeds',
    description: 'Client Other Medications',
    readRoles: ['admin', 'provider'],
    writeRoles: ['admin', 'provider'],
    phpPage: 'show_full_medical.php',
    requiresAdmin: false,
    userOwnership: false,
    notes: 'Legacy only, medical section, writePolicy: all'
  },

  dsm5dx: {
    fieldName: 'dsm5dx',
    description: 'Client DSM5DX',
    readRoles: ['admin', 'provider'],
    writeRoles: ['admin'],
    phpPage: 'show_full_medical.php',
    requiresAdmin: false,
    userOwnership: false,
    notes: 'ODR only, medical diagnosis, writePolicy: all'
  },

  // === DEMOGRAPHIC FIELDS ===

  name: {
    fieldName: 'name',
    description: 'Client Name',
    readRoles: ['admin', 'provider', 'provider+'],
    writeRoles: ['admin'],
    phpPage: 'edit_participant_demog.php',
    requiresAdmin: false,
    userOwnership: false,
    notes: 'Requires fn_linkpermission for edit_participant_demog.php, writePolicy: resolver'
  },

  dob: {
    fieldName: 'dob',
    description: 'Client Date of Birth',
    readRoles: ['admin', 'provider'],
    writeRoles: ['admin'],
    phpPage: 'edit_participant_demog.php',
    requiresAdmin: false,
    userOwnership: false,
    notes: 'Legacy only, demographic field, writePolicy: all'
  },

  gender: {
    fieldName: 'gender',
    description: 'Client Gender',
    readRoles: ['admin', 'provider'],
    writeRoles: ['admin'],
    phpPage: 'edit_participant_demog.php',
    requiresAdmin: false,
    userOwnership: false,
    notes: 'Legacy only, demographic field, writePolicy: all'
  },

  race: {
    fieldName: 'race',
    description: 'Client Race',
    readRoles: ['admin', 'provider'],
    writeRoles: ['admin'],
    phpPage: 'edit_participant_demog.php',
    requiresAdmin: false,
    userOwnership: false,
    notes: 'Legacy only, writePolicy: odr'
  },

  champid: {
    fieldName: 'champid',
    description: 'Client ChampID',
    readRoles: ['admin', 'provider', 'provider+'],
    writeRoles: ['admin'],
    phpPage: 'edit_participant_demog.php',
    requiresAdmin: false,
    userOwnership: false,
    notes: 'Both legacy and ODR, writePolicy: all'
  },

  address: {
    fieldName: 'address',
    description: 'Client Address',
    readRoles: ['admin', 'provider'],
    writeRoles: ['admin'],
    phpPage: 'edit_participant_demog.php',
    requiresAdmin: false,
    userOwnership: false,
    notes: 'ODR only'
  },

  // === CALAIM FIELDS ===

  ecmDetermination: {
    fieldName: 'ecmDetermination',
    description: 'Client ECM Determination', 
    readRoles: ['admin', 'provider', 'provider+'],
    writeRoles: ['admin'],
    phpPage: 'show_full_calaim.php',
    requiresAdmin: false,
    userOwnership: false,
    notes: 'ODR only, CalAIM section, writePolicy: odr'
  },

  ecmContacts: {
    fieldName: 'ecmContacts',
    description: 'List of ECM contacts',
    readRoles: ['admin', 'provider', 'provider+'],
    writeRoles: ['admin'],
    phpPage: 'show_full_calaim.php',
    requiresAdmin: false,
    userOwnership: false,
    notes: 'ODR only, CalAIM section, writePolicy: odr'
  },

  wpc: {
    fieldName: 'wpc',
    description: 'Client WPC',
    readRoles: ['admin', 'provider', 'provider+'],
    writeRoles: ['admin'],
    phpPage: 'show_full_calaim.php',
    requiresAdmin: false,
    userOwnership: false,
    notes: 'ODR only, CalAIM section'
  },

  healthplan: {
    fieldName: 'healthplan',
    description: 'Client Health Plan Information',
    readRoles: ['admin', 'provider', 'provider+'],
    writeRoles: ['admin'],
    phpPage: 'show_full_insurance_enrollment.php',
    requiresAdmin: false,
    userOwnership: false,
    notes: 'ODR only, insurance section, requires fn_linkpermission for show_full_insurance_enrollment.php'
  },

  // === NOTES (User Ownership) ===

  notes: {
    fieldName: 'notes',
    description: 'Notes Data',
    readRoles: ['admin', 'provider', 'provider+'],
    writeRoles: ['admin', 'provider', 'provider+'],
    phpPage: 'show_full_notes.php',
    requiresAdmin: false,
    userOwnership: true,
    notes: 'Notes can only be edited by their creator. Requires fn_linkpermission for show_full_notes.php'
  }
};

/**
 * Helper function to get security rules for a specific field
 */
export function getFieldSecurity(fieldName: string): FieldSecurityRule | undefined {
  return FIELD_SECURITY[fieldName];
}

/**
 * Helper function to check if a user has read access to a field
 */
export function canReadField(fieldName: string, userRoles: SecurityRole[]): boolean {
  const security = getFieldSecurity(fieldName);
  if (!security) return false;
  
  return security.readRoles.some(role => userRoles.includes(role));
}

/**
 * Helper function to check if a user has write access to a field  
 */
export function canWriteField(fieldName: string, userRoles: SecurityRole[]): boolean {
  const security = getFieldSecurity(fieldName);
  if (!security) return false;
  
  if (security.writeRoles.includes('none')) return false;
  
  return security.writeRoles.some(role => userRoles.includes(role));
}

/**
 * Helper function to get all fields accessible by a user's roles
 */
export function getAccessibleFields(userRoles: SecurityRole[], accessType: AccessLevel = 'read'): string[] {
  return Object.keys(FIELD_SECURITY).filter(fieldName => {
    if (accessType === 'read') {
      return canReadField(fieldName, userRoles);
    }
    if (accessType === 'write') {
      return canWriteField(fieldName, userRoles);
    }
    return false;
  });
}

export function getSecurityRolesFromPolicies(source: unknown): SecurityRole[] {
  const policyList = Array.isArray(source)
    ? normalizePolicyList(source)
    : normalizePolicyList((source as any)?.policies);
  const roles = new Set<SecurityRole>();

  if (policyList.includes('*:*~*') || hasAnyPolicy(policyList, ['admin:access~full'])) {
    roles.add('admin');
  }

  if (hasAnyPolicy(policyList, ['legacy:provider_plus'])) {
    roles.add('provider+');
    roles.add('provider');
  }

  if (hasAnyPolicy(policyList, [
    'legacy:provider',
    'tools:docslib:external~read',
    'tools:docslib:internal~read'
  ])) {
    roles.add('provider');
  }

  if (roles.size === 0) {
    roles.add('user');
  }

  return Array.from(roles);
}
